import type { WizardColorType } from '@gov-design-system-ce/components';
import { GovFormWeb } from '@gov-design-system-ce/react-form';
import { createStepTitleId, type FormWizardHook } from '@gov-nx/core/hooks';
import { Icon, WizardItem } from '@gov-nx/ui/web';
import { scrollToElement } from '@gov-nx/web';
import { useEffect } from 'react';

interface StepProps<T extends object> {
	label: string;
	index: number;
	size?: 's' | 'm' | 'l';
	children: React.ReactNode;
	wizard: FormWizardHook<T>;
}

export const WizardStepWeb = <T extends object>({
	index,
	wizard,
	label,
	size = 'l',
	children,
}: StepProps<T>) => {
	const step = wizard.step(index);

	useEffect(() => {
		const stepTitleElement = document.getElementById(
			createStepTitleId(index, wizard.wizardId)
		);
		if (!step.isFocused) return;
		if (!stepTitleElement) return;
		scrollToElement(stepTitleElement);
		stepTitleElement.focus({ preventScroll: true });
	}, [step.isFocused, wizard.focusTick, index, wizard.wizardId]);

	const getVariant = (): WizardColorType => {
		if (step.isCollapsible) {
			return 'success';
		}
		if (step.isExpanded) {
			return 'primary';
		}
		return 'neutral';
	};

	const getPrefix = (): React.ReactNode => {
		if (step.isCollapsible) {
			return (
				<Icon
					name="check-lg"
					type="components"
				/>
			);
		}
		return <span>{index + 1}</span>;
	};

	return (
		<WizardItem
			prefix={getPrefix()}
			headline={
				<h2
					tabIndex={-1}
					id={createStepTitleId(index, wizard.wizardId)}>
					{label}
				</h2>
			}
			isExpanded={step.isExpanded}
			isFocused={step.isFocused}
			color={getVariant()}
			size={size}
			onChange={(status) => {
				if (status) {
					wizard.openStep(index);
				} else {
					wizard.closeStep(index);
				}
			}}
			isCollapsible={step.isCollapsible}>
			{step.type === 'form' ? (
				<GovFormWeb
					formDefinition={step.formDefinition}
					onSubmit={step.onSubmit()}>
					{children}
				</GovFormWeb>
			) : (
				children
			)}
		</WizardItem>
	);
};
